import * as React from 'react'

import { cn } from '../../lib/utils'

interface Segment<T extends string> {
  value: T
  label: string
}

export interface SegmentedToggleProps<T extends string> {
  value: T
  segments: [Segment<T>, Segment<T>]
  onChange: (value: T) => void
  className?: string
}

/**
 * Two-option segmented control with a sliding active indicator.
 * Direction-aware (uses `inset-inline-start`) so the slide animates
 * correctly in both LTR and RTL.
 */
function SegmentedToggleInner<T extends string>(
  { value, segments, onChange, className }: SegmentedToggleProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const activeIndex = segments.findIndex((s) => s.value === value)

  return (
    <div
      ref={ref}
      role="tablist"
      className={cn(
        'relative inline-flex w-full max-w-[260px] rounded-lg border border-border bg-muted/30 p-1',
        className,
      )}
    >
      <span
        aria-hidden
        className="absolute inset-y-1 w-[calc(50%-0.25rem)] rounded-md bg-card shadow-sm ring-1 ring-border transition-[inset-inline-start] duration-300 ease-out"
        style={{ insetInlineStart: activeIndex <= 0 ? '0.25rem' : '50%' }}
      />
      {segments.map((segment) => {
        const isActive = segment.value === value
        return (
          <button
            key={segment.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(segment.value)}
            className={cn(
              'relative z-10 flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {segment.label}
          </button>
        )
      })}
    </div>
  )
}

// Cast preserves the generic signature that `forwardRef` would otherwise erase.
export const SegmentedToggle = React.forwardRef(SegmentedToggleInner) as <
  T extends string,
>(
  props: SegmentedToggleProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement
